import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getPublicPlans, type PublicPlan } from "../../services/api";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameCard, GameHero, GameIcon, Skeleton } from "../../components/game";
import { expandedFeatures } from "../../lib/planFeatures";

/**
 * Athlete-facing, INFORMATIVE plans screen. Reached from the "quiz locked"
 * upgrade modal. The actual upgrade/payment is done by the parent/guardian from
 * their own account, so this page explains that and lists the paid plans — it
 * does not itself start a checkout.
 */
export default function AppPlans() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // ALL plans are kept, not just the paid ones: a paid plan's feature list can
  // say "everything in Start", and resolving that needs the lower tiers (and
  // the free plan) to still be in hand. Only the paid ones are rendered.
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicPlans()
      .then((p) => setPlans(p))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const paidPlans = plans.filter((p) => !p.is_free);

  const money = (cents: number, currency: string) =>
    `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: cents % 100 ? 2 : 0 })} ${currency}`;

  return (
    <MobileAppLayout>
      <div className="pb-6">
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          <button
            onClick={() => navigate(-1)}
            className="-ml-1.5 mb-1.5 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label={t("common.back", "Înapoi")}
          >
            <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-extrabold">{t("plans.title", "Planuri")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">
            {t("plans.subtitle", "Deblochează toate quiz-urile și funcțiile")}
          </p>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
          {/* Upgrade is done by the parent. */}
          <div className="rounded-2xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-2.5">
            <svg className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-700">
              {t("plans.parentNote", "Abonamentul se gestionează din contul părintelui sau tutorelui tău. Roagă-l să facă upgrade-ul de acolo.")}
            </p>
          </div>

          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
            </div>
          ) : paidPlans.length === 0 ? (
            <GameCard className="p-8 text-center">
              <GameIcon name="star" className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-slate-500">{t("plans.empty", "Momentan nu există planuri disponibile.")}</p>
            </GameCard>
          ) : (
            <div className="space-y-3">
              {paidPlans.map((p) => (
                <GameCard key={p.code} className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-extrabold text-slate-800">{p.name}</h3>
                      {p.description && <p className="text-sm text-slate-500 mt-0.5">{p.description}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-black text-slate-800 tabular-nums leading-none">{money(p.price_cents_month, p.currency)}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">/ {t("common.month", "lună")}</p>
                    </div>
                  </div>
                  {p.price_cents_year != null && p.price_cents_year > 0 && (
                    <p className="text-xs text-emerald-600 font-semibold mt-2">
                      {t("plans.yearly", "sau {{price}} / an", { price: money(p.price_cents_year, p.currency) })}
                    </p>
                  )}
                  {/* The plan's OWN complete list — cross-references to
                      other plans resolved, so this card answers "what do I
                      get" without sending the reader to another one. */}
                  {expandedFeatures(p, plans).length > 0 && (
                    <ul className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                      {expandedFeatures(p, plans).map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                          <span className="w-5 h-5 rounded-full bg-emerald-100 grid place-items-center shrink-0 mt-0.5">
                            <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          </span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </GameCard>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-400 text-center px-4">
            {t("plans.footNote", "Cu un plan plătit ai acces la toate quiz-urile, nu doar la cele gratuite.")}
          </p>
        </div>
      </div>
    </MobileAppLayout>
  );
}
